import React from 'react';
import { Link } from 'react-router-dom';
import { Clock, ArrowRight, CheckCircle2 } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useCatalog } from '../context/CatalogContext';
import { getPopularTreatmentImage } from '../lib/imageOverrides';
import { buildLocalizedPath } from './LocaleRouter';

/**
 * "Popular Treatments in Vietnam" — curated 8-card grid.
 * Images sourced from Pexels (realistic, treatment-relevant healthcare imagery).
 * Each card: image, plain-English explanation, indicative price + recovery,
 * dual CTAs (Learn More + Request Treatment).
 */
const POPULAR = [
  {
    slug: 'dental-implants',
    name: 'Dental Implants',
    image: 'https://images.pexels.com/photos/305566/pexels-photo-305566.jpeg?auto=compress&cs=tinysrgb&w=900',
    desc: 'Permanent titanium-based tooth replacement that looks, feels and functions like a natural tooth.',
    price: 'From $450',
    recovery: 'Recovery: 3–6 months',
  },
  {
    slug: 'veneers',
    name: 'Veneers',
    image: 'https://images.pexels.com/photos/3845625/pexels-photo-3845625.jpeg?auto=compress&cs=tinysrgb&w=900',
    desc: 'Ultra-thin porcelain shells that perfect colour, shape and alignment of your front teeth.',
    price: 'From $250',
    recovery: 'Recovery: 5–7 days',
  },
  {
    slug: 'teeth-whitening',
    name: 'Teeth Whitening',
    image: 'https://images.pexels.com/photos/5069431/pexels-photo-5069431.jpeg?auto=compress&cs=tinysrgb&w=900',
    desc: 'Professional in-clinic whitening that brightens enamel by several shades in a single visit.',
    price: 'From $90',
    recovery: 'Recovery: Same day',
  },
  {
    slug: 'invisalign',
    name: 'Invisalign',
    image: 'https://images.pexels.com/photos/6627569/pexels-photo-6627569.jpeg?auto=compress&cs=tinysrgb&w=900',
    desc: 'Discreet teeth straightening with FDA-approved clear aligners — virtually invisible and removable.',
    price: 'From $1,800',
    recovery: 'Treatment: 6–18 months',
  },
  {
    slug: 'hair-transplant',
    name: 'Hair Transplant',
    image: 'https://images.pexels.com/photos/3993239/pexels-photo-3993239.jpeg?auto=compress&cs=tinysrgb&w=900',
    desc: 'Modern FUE and DHI techniques delivering natural, permanent results performed by board-certified surgeons.',
    price: 'From $1,800',
    recovery: 'Recovery: 7–14 days',
  },
  {
    slug: 'rhinoplasty',
    name: 'Rhinoplasty',
    image: 'https://images.pexels.com/photos/5327580/pexels-photo-5327580.jpeg?auto=compress&cs=tinysrgb&w=900',
    desc: 'Korean-style nose reshaping for elegant facial balance — performed by board-certified surgeons.',
    price: 'From $1,200',
    recovery: 'Recovery: 2 weeks',
  },
  {
    slug: 'botox-skin-care',
    name: 'Botox & Fillers',
    image: 'https://images.pexels.com/photos/3997988/pexels-photo-3997988.jpeg?auto=compress&cs=tinysrgb&w=900',
    desc: 'Premium neuromodulators and hyaluronic acid fillers for subtle, natural rejuvenation with no downtime.',
    price: 'From $120',
    recovery: 'Recovery: Same day',
  },
  {
    slug: 'skin-rejuvenation',
    name: 'Skin Treatments',
    image: 'https://images.pexels.com/photos/3985338/pexels-photo-3985338.jpeg?auto=compress&cs=tinysrgb&w=900',
    desc: 'Mesotherapy, laser facials and medical-grade rejuvenation programmes from leading dermatologists.',
    price: 'From $150',
    recovery: 'Recovery: Same day',
  },
];

const PopularTreatments = () => {
  const { t: tr, language } = useLanguage();
  const { overrides, getTreatmentBySlug } = useCatalog();

  // Resolve the final image URL for each popular card.
  // Priority: overrides.popularTreatments[slug] → overrides.treatments[slug]
  //           → merged catalog treatment.image (also already runs through
  //             overrides on the API side) → bundled static image.
  const resolvePopularImage = (slug, staticFallback) => {
    const merged = getTreatmentBySlug?.(slug);
    return getPopularTreatmentImage(overrides, slug, merged?.image || staticFallback);
  };
  return (
  <section className="py-20 lg:py-24 bg-white" data-testid="popular-treatments-section">
    <div className="container mx-auto px-4 max-w-7xl">
      <div className="max-w-3xl mb-12 lg:mb-14">
        <p className="text-[11px] font-bold text-[#1FA7A1] uppercase tracking-widest mb-3">
          {tr('popular.eyebrow')}
        </p>
        <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-slate-900 tracking-tight leading-[1.1]">
          {tr('popular.title')}
        </h2>
        <p className="text-slate-500 mt-4 text-base lg:text-lg leading-relaxed">
          {tr('popular.subtitle')}
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6">
        {POPULAR.map((t) => (
          <article
            key={t.slug}
            className="bg-white rounded-[24px] border border-slate-100 overflow-hidden flex flex-col hover:-translate-y-1 hover:shadow-xl transition-all duration-300"
            style={{ boxShadow: '0 4px 20px rgba(15,42,85,0.05)' }}
            data-testid={`popular-treatment-${t.slug}`}
          >
            <div className="relative aspect-[4/3] overflow-hidden bg-slate-50">
              <img
                src={resolvePopularImage(t.slug, t.image)}
                alt={t.name}
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                loading="lazy"
                onError={(e) => { e.target.src = t.image; }}
              />
            </div>

            <div className="p-5 lg:p-6 flex flex-col flex-1">
              <h3 className="font-extrabold text-slate-900 text-lg leading-snug mb-2">
                {t.name}
              </h3>
              <p className="text-sm text-slate-500 leading-relaxed mb-4 flex-1">
                {t.desc}
              </p>

              <div className="flex flex-col gap-1.5 mb-5">
                <p className="text-sm font-bold text-[#1FA7A1] flex items-center gap-1.5">
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  {t.price}
                </p>
                <p className="text-xs text-slate-400 font-medium flex items-center gap-1.5">
                  <Clock className="h-3.5 w-3.5" />
                  {t.recovery}
                </p>
              </div>              <div className="flex flex-col gap-2 mt-auto">
                <Link
                  to={buildLocalizedPath(`/treatments/${t.slug}`, language)}
                  className="inline-flex items-center justify-center gap-1.5 w-full text-sm font-semibold text-slate-700 border border-slate-200 hover:border-slate-300 hover:bg-slate-50 rounded-full py-2.5 transition-all"
                  data-testid={`popular-treatment-learn-${t.slug}`}
                  aria-label={`${t.name} in Vietnam — full treatment guide`}
                >
                  {t.name} in Vietnam
                  <ArrowRight className="h-3.5 w-3.5" />
                </Link>
                <Link
                  to={buildLocalizedPath(`/treatments/${t.slug}#request-appointment`, language)}
                  className="inline-flex items-center justify-center gap-1.5 w-full text-sm font-bold text-white rounded-full py-2.5 transition-all"
                  style={{ background: 'linear-gradient(135deg, #1FA7A1 0%, #178f8a 100%)' }}
                  data-testid={`popular-treatment-request-${t.slug}`}
                >
                  {tr('common.requestTreatment')}
                </Link>
              </div>
            </div>
          </article>
        ))}
      </div>

      <div className="text-center mt-12">
        <Link
          to={buildLocalizedPath('/treatments', language)}
          className="inline-flex items-center gap-2 text-[#1FA7A1] font-semibold hover:gap-3 transition-all text-sm"
          data-testid="popular-treatments-view-all"
        >
          {tr('popular.viewAllTreatments')} <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </div>
  </section>
  );
};

export default PopularTreatments;
